import AuthorizationHelper from '@auth/services/authorization.helper';
import ModulesHelper from '@core/services/modules.helper';
import store from '@app/store';
import AuthService from '@auth/services/auth.service';
import AuthorizationService from '@auth/services/authorization.service';
import { appConfig } from '@/config/app-config';

const redirectToFrontOfficeV2IfNeeded = async (to, next, token, expiresAt) => {
  if (typeof window === 'undefined') {
    return next();
  }

  const isFrontOfficeV2 = store.getters['tenant/tenantFrontOfficeV2Enabled'];
  if (!isFrontOfficeV2) {
    return next();
  }

  const tenantAlias: string | undefined = store.getters['tenant/tenantAlias'];
  const baseUrl: string | undefined = appConfig?.frontofficeV2Url;

  if (!tenantAlias || !baseUrl) {
    return next();
  }

  const subPath = to.meta?.frontOfficeV2SubPath;

  const tenantUrl = new URL(
    `/site/${encodeURIComponent(tenantAlias)}${subPath ? `/${subPath}` : ''}`,
    baseUrl,
  );

  const params = new URLSearchParams(tenantUrl.search);
  const fromURL = `${window.location.origin}${to.fullPath}`;
  params.set('redirectedFrom', fromURL);

  if (token) {
    params.set('qAuth', token);
  }
  if (expiresAt !== undefined) {
    params.set('expiresAt', String(expiresAt));
  }

  tenantUrl.search = params.toString();
  window.location.href = tenantUrl.toString();
};

export const mainContactsRoutes = [
  {
    path: 'contacts',
    name: 'admin-contacts',
    meta: {
      layout: 'bpc-admin',
      withSidebar: true,
      permissions: [AuthorizationHelper.fixedPermissions.sidebar.contacts],
      module: ModulesHelper.CONTACTS_MODULE_NAME,
    },
    component: () =>
      import(/* webpackChunkName: "admin-contacts" */ '@contacts/pages/contacts/contacts.vue'),
  },
  {
    path: 'club-contacts',
    name: 'admin-club-contacts',
    meta: {
      layout: 'bpc-admin',
      withSidebar: true,
      permissions: [AuthorizationHelper.fixedPermissions.sidebar.contacts],
      module: ModulesHelper.CONTACTS_MODULE_NAME,
    },
    component: () =>
      import(/* webpackChunkName: "admin-club-contacts" */ '@contacts/pages/club-contacts.vue'),
  },
  {
    path: 'club-team-and-groups',
    name: 'admin-club-team-and-groups',
    meta: {
      layout: 'bpc-admin',
      withSidebar: true,
      permissions: [AuthorizationHelper.fixedPermissions.sidebar.contacts],
      module: ModulesHelper.CONTACTS_MODULE_NAME,
    },
    component: () =>
      import(
        /* webpackChunkName: "admin-club-team-and-groups" */ '@contacts/pages/club-team-and-groups.vue'
      ),
  },
  {
    path: 'contacts/teams',
    name: 'admin-contacts-teams',
    meta: {
      layout: 'bpc-admin',
      withSidebar: true,
      permissions: [AuthorizationHelper.fixedPermissions.sidebar.contacts],
      module: ModulesHelper.CONTACTS_MODULE_NAME,
      contactType: 'team',
    },
    props: true,
    component: () =>
      import(/* webpackChunkName: "admin-contacts" */ '@contacts/pages/contacts/contacts.vue'),
  },
  {
    path: 'contacts/clubs',
    name: 'admin-contacts-clubs',
    meta: {
      layout: 'bpc-admin',
      withSidebar: true,
      permissions: [AuthorizationHelper.fixedPermissions.sidebar.contacts],
      module: ModulesHelper.CONTACTS_MODULE_NAME,
      contactType: 'club',
    },
    props: true,
    component: () =>
      import(/* webpackChunkName: "admin-contacts" */ '@contacts/pages/contacts/contacts.vue'),
  },
  {
    path: 'contacts/:contact_id',
    name: 'admin-contact-show',
    meta: {
      layout: 'bpc-admin',
      withSidebar: true,
      originShowContact: true,
      module: ModulesHelper.CONTACTS_MODULE_NAME,
    },
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "admin-contacts" */ '@contacts/pages/contact-show/contact-show.vue'
      ),
  },
  {
    path: 'contacts/me/:contact_id',
    name: 'admin-my-contact-show',
    meta: {
      layout: 'bpc-admin',
      withSidebar: true,
      originShowContact: false,
      module: ModulesHelper.CONTACTS_MODULE_NAME,
    },
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "admin-contacts" */ '@contacts/pages/contact-show/contact-show.vue'
      ),
  },
  {
    path: 'user-evolution',
    name: 'admin-user-evolution',
    meta: {
      layout: 'bpc-admin',
      withSidebar: true,
      permissions: [AuthorizationHelper.fixedPermissions.sidebar.user_evolution],
      module: ModulesHelper.CONTACTS_MODULE_NAME,
    },
    component: () =>
      import(/* webpackChunkName: "admin-contacts" */ '@contacts/pages/user-evolution-page.vue'),
  },
  {
    path: 'contacts-tppwb',
    name: 'admin-contacts-tppwb',
    meta: {
      layout: 'bpc-admin',
      withSidebar: true,
      permissions: [AuthorizationHelper.fixedPermissions.sidebar.contacts],
      module: ModulesHelper.TPPWB_SYNC_MODULE_NAME,
    },
    component: () =>
      import(
        /* webpackChunkName: "admin-contacts-tppwb" */ '@contacts/pages/contacts-tppwb/contacts-tppwb.vue'
      ),
  },
];

export const configurationContactsRoutes = [
  {
    path: 'price-categories',
    name: 'admin-price-categories',
    meta: {
      layout: 'bpc-admin',
      withSidebar: true,
      permissions: [AuthorizationHelper.fixedPermissions.sidebar.price_categories],
      module: ModulesHelper.CONTACTS_MODULE_NAME,
    },
    component: () =>
      import(
        /* webpackChunkName: "admin-price-categories" */ '@contacts/pages/price-categories/price-categories.vue'
      ),
  },
  {
    path: 'contact-relation-categories',
    name: 'admin-contact-relation-categories',
    meta: {
      layout: 'bpc-admin',
      withSidebar: TextTrackCue,
      permissions: [AuthorizationHelper.fixedPermissions.sidebar.contact_relation_categories],
      module: ModulesHelper.CONTACTS_MODULE_NAME,
    },
    component: () =>
      import(
        /* webpackChunkName: "admin-price-categories" */ '@contacts/pages/contact-relation-categories/contact-relation-categories.vue'
      ),
  },
  {
    path: 'contact-tags',
    name: 'admin-contact-tags',
    meta: {
      layout: 'bpc-admin',
      withSidebar: true,
      permissions: [AuthorizationHelper.fixedPermissions.sidebar.contact_tags],
      module: ModulesHelper.CONTACTS_MODULE_NAME,
    },
    component: () =>
      import(
        /* webpackChunkName: "admin-contact-tags" */ '@contacts/pages/contact-tags/contact-tags.vue'
      ),
  },
  {
    path: 'contact-relations',
    name: 'admin-contact-relations',
    meta: {
      layout: 'bpc-admin',
      withSidebar: true,
      permissions: [AuthorizationHelper.fixedPermissions.sidebar.contact_relations],
      module: ModulesHelper.CONTACTS_MODULE_NAME,
    },
    component: () =>
      import(
        /* webpackChunkName: "admin-price-categories" */ '@contacts/pages/contact-relations/contact-relations.vue'
      ),
  },
];

export const profileContactRoutes = [
  {
    path: 'profile',
    name: 'contact-profile-dashboard',
    meta: {
      layout: 'bpc-public',
      withSidebar: true,
      originShowContact: false,
      frontOfficeV2SubPath: 'home',
    },
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "contact-profile-dashboard" */ '@contacts/pages/contact-profile/contact-profile.vue'
      ),
    beforeEnter: async (to, from, next) => {
      const $authService = new AuthService();
      const $authorizationService = new AuthorizationService();
      if (!$authService.isLoggedIn()) {
        const loginRoute = $authorizationService.getAuthLoginRoute(to);
        next(loginRoute);
      }
      const isIndividual = $authService.getIsIndividual();
      if (isIndividual) {
        await redirectToFrontOfficeV2IfNeeded(
          to,
          next,
          $authService.getToken(),
          $authService.getExpiresAt(),
        );
      }
      // If the entry is not in the select from center settings, deny the access to that route
      if (to.meta?.menuEntry) {
        const showMenuEntries = store.getters['centerSettings/centerSettings']?.show_menu_entries;
        if (showMenuEntries && showMenuEntries.includes(to.meta?.menuEntry)) {
          next();
        }
      } else {
        next();
      }
      if (to.name === 'contact-profile-dashboard') {
        next();
      }
    },

    children: [
      {
        path: 'contact',
        name: 'profile-contact',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
          menuEntry: 'contact',
          frontOfficeV2SubPath: 'profile/account',
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-contact" */ '@contacts/pages/contact-profile/profile-contact/profile-contact.vue'
          ),
      },
      {
        path: 'mailbox',
        name: 'profile-mailbox',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
          menuEntry: 'mailbox',
          markAsReadMessages: true,
          frontOfficeV2SubPath: 'home',
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-mailbox" */ '@contacts/pages/contact-profile/profile-mailbox/profile-mailbox.vue'
          ),
      },
      {
        path: 'bookings',
        name: 'profile-bookings',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
          menuEntry: 'bookings',
          frontOfficeV2SubPath: 'profile/bookings',
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-bookings" */ '@contacts/pages/contact-profile/profile-bookings/profile-bookings.vue'
          ),
      },
      {
        path: 'my-booking-requests',
        name: 'profile-my-booking-request',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
          menuEntry: 'my-booking-requests',
          frontOfficeV2SubPath: 'home',
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-bookings" */ '@contacts/pages/contact-profile/profile-booking-requests/profile-booking-requests.vue'
          ),
      },
      {
        path: 'new-booking',
        name: 'profile-new-booking',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
          frontOfficeV2SubPath: 'bookings/online-booking?step=discipline_selector',
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-new-booking" */ '@contacts/pages/contact-profile/profile-new-booking/profile-new-booking.vue'
          ),
      },
      {
        path: 'online-booking',
        name: 'profile-online-booking',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
          menuEntry: 'bookings',
          module: ModulesHelper.ONLINE_BOOKING_V2_MODULE_NAME,
          frontOfficeV2SubPath: 'bookings/online-booking?step=discipline_selector',
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-online-booking" */ '@contacts/pages/contact-profile/profile-online-booking-page.vue'
          ),
      },
      {
        path: 'new-organization-booking',
        name: 'profile-new-organization-booking',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-new-booking" */ '@contacts/pages/contact-profile/profile-new-organization-booking/profile-new-organization-booking.vue'
          ),
      },
      {
        path: 'enrollments',
        name: 'profile-enrollments',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
          menuEntry: 'enrollments',
          frontOfficeV2SubPath: 'profile/activities',
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-enrollments" */ '@contacts/pages/contact-profile/profile-enrollments/profile-enrollments.vue'
          ),
      },
      {
        path: 'subscriptions',
        name: 'profile-subscriptions',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
          menuEntry: 'subscriptions',
          frontOfficeV2SubPath: 'profile/subscriptions',
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-subscriptions" */ '@contacts/pages/contact-profile/profile-subscriptions/profile-subscriptions.vue'
          ),
      },
      {
        path: 'invoices',
        name: 'profile-invoices',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
          menuEntry: 'invoices',
          frontOfficeV2SubPath: 'profile/purchase-history',
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-invoices" */ '@contacts/pages/contact-profile/profile-invoices/profile-invoices.vue'
          ),
      },
      {
        path: 'orders',
        name: 'profile-orders',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
          menuEntry: 'orders',
          frontOfficeV2SubPath: 'profile/purchase-history',
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-orders" */ '@contacts/pages/contact-profile/profile-orders/profile-orders.vue'
          ),
      },
      {
        path: 'activities',
        name: 'profile-activities',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
          menuEntry: 'activities',
          frontOfficeV2SubPath: 'activities',
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-activities" */ '@contacts/pages/contact-profile/profile-activities/profile-activities.vue'
          ),
      },
      {
        path: 'activity-sessions',
        name: 'profile-activity-sessions',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
          menuEntry: 'activity-sessions',
          frontOfficeV2SubPath: 'profile/activities',
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-activity-sessions" */ '@activities/pages/profile-activity-sessions.vue'
          ),
      },
      {
        path: 'attendees',
        name: 'profile-attendees',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
          menuEntry: 'attendees',
          frontOfficeV2SubPath: 'profile/activities',
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-activity-sessions" */ '@activities/pages/profile-attendees.vue'
          ),
      },
      {
        path: 'contracts',
        name: 'profile-contracts',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
          menuEntry: 'contracts',
          frontOfficeV2SubPath: 'home',
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-contracts" */ '@contacts/pages/contact-profile/profile-contracts/profile-contracts.vue'
          ),
      },
      {
        path: 'edit-password',
        name: 'profile-edit-password',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
          menuEntry: 'edit-password',
          frontOfficeV2SubPath: 'home',
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "admin-profiles" */ '@auth/pages/edit-password/edit-password.vue'
          ),
      },
      {
        path: 'activity-details/:alias',
        name: 'profile-activity-details',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
          menuEntry: 'activities',
          frontOfficeV2SubPath: 'activities',
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-activity-details" */ '@contacts/pages/contact-profile/profile-activity-details/profile-activity-details.vue'
          ),
        children: [
          {
            path: 'posts/:postId',
            name: 'profile-activity-details-post',
            meta: {
              layout: 'bpc-public',
              withSidebar: true,
              originShowContact: false,
            },
            props: true,
            component: () =>
              import(
                /* webpackChunkName: "contact-profile-activity-details" */ '@contacts/pages/contact-profile/profile-activity-details/profile-activity-details.vue'
              ),
          },
        ],
      },
      {
        path: 'subscription-category-details/:subscriptionCategoryId',
        name: 'profile-subscription-category-details',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
          menuEntry: 'subscriptions-available',
          frontOfficeV2SubPath: 'subscriptions',
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-subscription-category-details" */ '@contacts/pages/contact-profile/profile-subscription-category-details/profile-subscription-category-details.vue'
          ),
        children: [],
      },
      {
        path: 'family',
        name: 'profile-family',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
          menuEntry: 'family',
          frontOfficeV2SubPath: 'profile/family',
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-family" */ '@contacts/pages/contact-profile/profile-family/profile-family.vue'
          ),
      },
      {
        path: 'family-form/:id?',
        name: 'profile-family-form',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
          menuEntry: 'family',
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-family-form" */ '@contacts/pages/contact-profile/profile-family-form/profile-family-form.vue'
          ),
      },
      {
        path: 'shopping-cart',
        name: 'profile-shopping-cart',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-shopping-cart" */ '@accounting/pages/shopping-cart-page.vue'
          ),
      },
      // {
      //   path: 'invoice-shopping-cart/:token',
      //   name: 'profile-invoice-shopping-cart',
      //   meta: { layout: 'bpc-public', withSidebar: false },
      //   props: true,
      //   component: () =>
      //     import(
      //       /* webpackChunkName: "profile-invoice-shopping-cart" */ '@accounting/pages/invoice-shopping-cart-page.vue'
      //     ),
      // },
      {
        path: 'payment-confirm',
        name: 'payment-confirmation-page',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-payment-confirmation-page" */ '@accounting/pages/payment-confirmation-page.vue'
          ),
      },
      {
        path: 'payment-cancel',
        name: 'profile-payment-cancel',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-payment-cancel-page" */ '@accounting/pages/payment-cancel-page.vue'
          ),
      },
      {
        path: 'payment-invoice/:token',
        name: 'profile-payment-invoice',
        meta: { layout: 'bpc-public', withSidebar: false },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "payment-invoice" */ '@accounting/pages/invoice-shopping-cart-page.vue'
          ),
      },
      {
        path: 'subscriptions-available',
        name: 'profile-subscriptions-available',
        meta: {
          layout: 'bpc-public',
          withSidebar: true,
          originShowContact: false,
          menuEntry: 'subscriptions-available',
          frontOfficeV2SubPath: 'subscriptions',
        },
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "contact-profile-subscriptions" */ '@contacts/pages/contact-profile/profile-subscriptions-available/profile-subscriptions-available.vue'
          ),
      },
    ],
  },
];

export const publicContactRoutes = [
  {
    path: 'new-contact',
    name: 'public-new-contact',
    meta: {
      layout: 'bpc-auth',
      publicRoute: true,
      entity: 'contact',
    },
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "public-new-contact" */ '@contacts/pages/new-contact/new-contact.vue'
      ),
  },
];
